import { useState, useEffect, useCallback, useRef } from "react";
import { ref, onValue } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { database, functions } from "../firebaseConfig";
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import { costOf } from '../config/creditPlans';
import { useResearchLicenseRef } from '../hooks/useResearchAccess';
import {
  potentialProfilesPath,
  potentialLegacyPath,
  profilesFromSnapshot,
  resolvePotentialProfile,
  buildPotentialIcp,
} from './potentialProfiles';
import { recordCompanyRecord } from '../shared/recordIntelligenceLog';

const POTENTIAL_COST = costOf('potentialAnalyzer');

const DEFAULT_PA_SETTINGS = {
  enabled: false,
  description: '',
  revenueSize: '',
  employeeMin: '',
  employeeMax: '',
  strictness: 'balanced',
  language: 'auto',
  mode: 'combo',
};

// The analyser appends a short reasoning block to lead_description. We mark it
// with a recognizable prefix so a re-run replaces the old note instead of
// stacking duplicates. The score itself lives in its own `potential_score`
// field and is never stored inside the description.
const POTENTIAL_NOTE_RE = /\s*\[Potential\s+\d+\/10\][\s\S]*$/i;

function buildDescriptionWithNote(currentDescription, score, summary) {
  if (!summary) return null;
  const base = String(currentDescription || '').replace(POTENTIAL_NOTE_RE, '').trimEnd();
  const note = `[Potential ${score}/10] ${summary}`;
  return base ? `${base}\n\n${note}` : note;
}

function buildPayload(rowData, profile) {
  return {
    businessName: rowData.business_name || '',
    websiteUrl: rowData.lead_website || '',
    description: rowData.lead_description || '',
    email: rowData.lead_email || '',
    city: rowData.lead_city || '',
    country: rowData.lead_country || '',
    icp: buildPotentialIcp(profile),
    language: profile.language || 'auto',
    // Smart Combo is the only engine now — every analysis (incl. legacy settings
    // that still say 'high-performance'/'normal') runs the cheap, consistent
    // Serper + DeepSeek pipeline.
    mode: 'combo',
  };
}

export default function usePotentialAnalyzer({
  userId,
  handleEdit,
  displayRows,
  saveScrollPosition,
  restoreScrollPosition,
}) {
  // Letzte Sicherung vor dem Function-Call — die Dialoge sperren bereits.
  const potentialLicensed = useResearchLicenseRef('hasPotentialAnalyzer');

  const [potentialLoadingRows, setPotentialLoadingRows] = useState({});
  const [paSettings, setPaSettings] = useState(DEFAULT_PA_SETTINGS);
  // Benannte ICP-Profile. `paSettings` bleibt Hauptschalter (`enabled`) und
  // Fallback-ICP für Worlds, die noch kein Profil angelegt haben.
  const [potentialProfiles, setPotentialProfiles] = useState([]);
  const [bulkProgress, setBulkProgress] = useState(null);

  const displayRowsRef = useRef(displayRows);
  const paSettingsRef = useRef(paSettings);
  const profilesRef = useRef(potentialProfiles);
  const bulkDismissTimerRef = useRef(null);
  useEffect(() => { displayRowsRef.current = displayRows; }, [displayRows]);
  useEffect(() => { paSettingsRef.current = paSettings; }, [paSettings]);
  useEffect(() => { profilesRef.current = potentialProfiles; }, [potentialProfiles]);
  useEffect(() => () => clearTimeout(bulkDismissTimerRef.current), []);

  useEffect(() => {
    if (!userId) return undefined;
    const settingsRef = ref(database, potentialLegacyPath(userId));
    const unsubSettings = onValue(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        setPaSettings(prev => ({ ...prev, ...snapshot.val() }));
      } else {
        setPaSettings(DEFAULT_PA_SETTINGS);
      }
    });

    const profilesRefDb = ref(database, potentialProfilesPath(userId));
    const unsubProfiles = onValue(profilesRefDb, (snapshot) => {
      setPotentialProfiles(profilesFromSnapshot(snapshot.exists() ? snapshot.val() : null));
    });

    return () => { unsubSettings(); unsubProfiles(); };
  }, [userId]);

  // Welches Profil ein Lauf nutzt: explizite Wahl → Standard-Profil mit ICP →
  // Alt-Config. Einzelläufe über das Research-Menü übergeben kein profileId und
  // laufen damit weiter wie vor dem Multi-Umzug (Default oder Legacy).
  const pickProfile = useCallback((profileId) =>
    resolvePotentialProfile(profilesRef.current, paSettingsRef.current, profileId), []);

  // Persist a returned analysis onto the contact: score + analysis object +
  // reasoning appended to the description (never the score).
  const applyAnalysis = useCallback(async (rowData, analysis) => {
    const score = analysis?.score;
    if (typeof score !== 'number') throw new Error('No score in analysis');

    const analysisObj = {
      score,
      fitLabel: analysis.fitLabel || '',
      summary: analysis.summary || '',
      bullets: Array.isArray(analysis.bullets) ? analysis.bullets : [],
      sizeAssessment: analysis.sizeAssessment || 'unknown',
      responseChance: analysis.responseChance || null,
      // "none" = the AI could barely find anything → the low score reflects a
      // disadvantage, not a neutral middle. The UI marks this case with its own
      // icon so a 2-3 here reads as "no info" rather than "weak fit".
      dataConfidence: analysis.dataConfidence || 'medium',
      analyzedAt: analysis.analyzedAt || new Date().toISOString(),
    };

    await handleEdit(rowData.id, 'potential_score', score);
    await handleEdit(rowData.id, 'potential_analysis', analysisObj);
    await recordCompanyRecord(userId, rowData.id, 'potential', rowData, {
      ...rowData,
      potential_score: score,
      potential_analysis: analysisObj,
    });

    const newDescription = buildDescriptionWithNote(rowData.lead_description, score, analysis.summary);
    if (newDescription !== null) {
      await handleEdit(rowData.id, 'lead_description', newDescription);
    }
  }, [handleEdit, userId]);

  const deductOne = useCallback(async (rowData) => {
    const deductCreditsFn = httpsCallable(functions, 'deductCredits');
    await deductCreditsFn({
      action: 'potential_analyzer',
      amount: POTENTIAL_COST,
      worldId: userId,
      metadata: { contactId: rowData.id },
    });
  }, [userId]);

  const handleAnalyzePotentialClick = useCallback(async (index, profileId = null) => {
    if (!potentialLicensed.current) return;
    saveScrollPosition();
    const rowData = displayRowsRef.current[index];
    if (!rowData) { alert("Keine Daten für diesen Eintrag vorhanden."); return; }
    if (!rowData.business_name?.trim() && !rowData.lead_website?.trim()) {
      alert("Business-Name oder Website-URL erforderlich für die Potential-Analyse.");
      return;
    }

    if (!paSettingsRef.current.enabled) {
      alert("Bitte aktiviere den Potential Analyser zuerst in den Research-Einstellungen.");
      return;
    }
    const profile = pickProfile(profileId);

    setPotentialLoadingRows(prev => ({ ...prev, [rowData.id]: true }));
    try {
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.analyzePotential(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload(rowData, profile)),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => null);
        // 402 = AI provider (Grok) out of credits — not the user's fault, so we
        // never charge for it and show a clear message.
        if (response.status === 402 || errBody?.error === 'ai_provider_unavailable') {
          alert(errBody?.details || 'Der KI-Anbieter ist aktuell nicht verfügbar (kein Guthaben). Es wurden keine Credits abgezogen.');
          return;
        }
        throw new Error(errBody?.details || errBody?.error || `Analyse fehlgeschlagen (${response.status})`);
      }

      const result = await response.json();
      if (!result.success || !result.analysis) {
        throw new Error("Keine Analyse-Daten erhalten");
      }

      // Only charge after a successful analysis so failed calls are free.
      try {
        await deductOne(rowData);
      } catch (creditError) {
        const msg = creditError?.message || '';
        if (msg.includes('Not enough credits') || creditError?.code === 'resource-exhausted') {
          alert(`Nicht genug Credits für den Potential Analyser (${POTENTIAL_COST} Credits benötigt).`);
          return;
        }
        throw creditError;
      }

      await applyAnalysis(rowData, result.analysis);
    } catch (error) {
      console.error("[POTENTIAL] Error:", error);
      alert(`Fehler bei der Potential-Analyse: ${error.message}`);
    } finally {
      setPotentialLoadingRows(prev => { const n = { ...prev }; delete n[rowData.id]; return n; });
      setTimeout(restoreScrollPosition, 50);
    }
  }, [saveScrollPosition, restoreScrollPosition, applyAnalysis, deductOne, potentialLicensed, pickProfile]);

  const handleStartBulkPotential = useCallback(async (contactIds, creditBalance, profileId = null) => {
    if (!potentialLicensed.current) return;
    if (!paSettingsRef.current.enabled) {
      alert("Bitte aktiviere den Potential Analyser zuerst in den Research-Einstellungen.");
      return;
    }
    const profile = pickProfile(profileId);

    const contacts = displayRowsRef.current
      .filter(row => row?.id && contactIds.includes(row.id))
      .filter(row => row.business_name?.trim() || row.lead_website?.trim());

    if (contacts.length === 0) {
      alert("Keiner der ausgewählten Kontakte hat einen Business-Namen oder eine Website.");
      return;
    }

    if (typeof creditBalance === 'number') {
      const affordable = Math.floor(creditBalance / POTENTIAL_COST);
      if (affordable < contacts.length) {
        const proceed = window.confirm(
          `Wenig Credits: Du hast ${creditBalance} Credits (${POTENTIAL_COST} pro Analyse).\n` +
          `Nur ${affordable} von ${contacts.length} Kontakten können analysiert werden.\n\nTrotzdem fortfahren?`
        );
        if (!proceed) return;
        contacts.splice(affordable);
      }
    }

    setBulkProgress({ total: contacts.length, done: 0, failed: 0 });
    let done = 0;
    let failed = 0;
    // When the AI provider is unavailable (both the High-Performance engine and
    // its Sonar fallback fail) we abort the whole run instead of hammering it.
    let providerDown = false;

    // Limited concurrency so we stay friendly to the web-search backend.
    const CONCURRENCY = 3;
    let cursor = 0;

    const worker = async () => {
      while (cursor < contacts.length && !providerDown) {
        const rowData = contacts[cursor++];
        setPotentialLoadingRows(prev => ({ ...prev, [rowData.id]: true }));
        try {
          const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.analyzePotential(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(buildPayload(rowData, profile)),
          });
          if (response.status === 402) {
            providerDown = true;
            break;
          }
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const result = await response.json();
          if (result.success && result.analysis) {
            // Charge only after a successful analysis so failures stay free.
            await deductOne(rowData);
            await applyAnalysis(rowData, result.analysis);
            done++;
          } else {
            failed++;
          }
        } catch (err) {
          console.error('[POTENTIAL-BULK] Error:', err);
          failed++;
        } finally {
          setPotentialLoadingRows(prev => { const n = { ...prev }; delete n[rowData.id]; return n; });
          setBulkProgress({ total: contacts.length, done, failed });
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, contacts.length) }, worker));

    if (providerDown) {
      setPotentialLoadingRows({});
      alert('Der KI-Anbieter ist aktuell nicht verfügbar (kein Guthaben). Die Bulk-Analyse wurde gestoppt – für fehlgeschlagene Kontakte wurden keine Credits abgezogen.');
    }

    setBulkProgress(prev => prev ? { ...prev, done, failed, finished: true } : null);
    bulkDismissTimerRef.current = setTimeout(() => setBulkProgress(null), 5000);
  }, [applyAnalysis, deductOne, potentialLicensed, pickProfile]);

  return {
    potentialLoadingRows,
    paSettings,
    potentialProfiles,
    bulkProgress,
    handleAnalyzePotentialClick,
    handleStartBulkPotential,
  };
}
